/**
 * Stock Analyzer navigation structure.
 *
 * Two sections that reflect how the destinations actually behave:
 *
 *  - ANALYSIS: driven by the Analysis Input. Submitting tickers there populates
 *    these tabs, and most of them are dead ends until you do. Options Explorer
 *    belongs here because it reads the analyzed tickers rather than asking for
 *    its own.
 *  - TOOLS: self-contained mini-apps. Each has its own inputs and ignores the
 *    analysis input entirely.
 *
 * Keys are load-bearing: `renderTabContent()` switches on them and
 * `handleAnalysisSubmit` maps snake_case analysis_types onto the kebab-case
 * keys below with bare string literals. Renaming a key silently breaks both.
 */
export const NAV_SECTIONS = [
  {
    title: 'Analysis',
    items: [
      { key: 'input', label: 'Analysis Input', icon: '🔍' },
      { key: 'fundamentals', label: 'Fundamentals', icon: '📊' },
      { key: 'eps-trends', label: 'EPS Trends', icon: '📈' },
      { key: 'financials', label: 'Financials', icon: '📉' },
      { key: 'fair-value', label: 'Fair Value', icon: '💰' },
      { key: 'comparables', label: 'Comparables', icon: '📏' },
      { key: 'company-score', label: 'Company Score', icon: '🎯' },
      { key: 'earnings', label: 'Earnings', icon: '📅' },
      { key: 'explorer', label: 'Options Explorer', icon: '🔭' },
    ],
  },
  {
    title: 'Tools',
    items: [
      { key: 'scalping', label: 'Options Scalper', icon: '🎯' },
      { key: 'spreads', label: 'Spread Simulator', icon: '📐' },
      { key: 'straddles', label: 'Straddle Simulator', icon: '🦋' },
      { key: 'elasticity', label: 'Options Elasticity', icon: '⚡' },
      { key: 'hedge', label: 'Hedge / Protect', icon: '🛡️' },
      { key: 'macro', label: 'Macro Dashboard', icon: '🌐' },
      { key: 'history', label: 'History', icon: '📋' },
    ],
  },
];

/** Every valid tab key — used to validate the ?tab= URL param. */
export const TAB_KEYS = new Set(
  NAV_SECTIONS.flatMap((section) => section.items.map((item) => item.key))
);

/** The tab shown when no (or an unrecognized) ?tab= value is present. */
export const DEFAULT_TAB = 'input';

/** Look up an item by key, for labelling the mobile dropdown button. */
export const findNavItem = (key) => {
  for (const section of NAV_SECTIONS) {
    const match = section.items.find((item) => item.key === key);
    if (match) return match;
  }
  return null;
};
